
































































































































































































import { PropType } from 'vue';
import { AxiosError } from 'axios';
import moment from 'moment';
import { mapActions } from 'vuex';
import { get } from 'vuex-pathify';
import { Transition } from '@/models/transition';
import { ErrorManager } from '@/models/error';
import ErrorAlert from '../common/ErrorAlert.vue';
import auth from '@/mixins/auth';
import colorable from '@/mixins/colorable';
import mixins from '@/utils/mixins';
import { isHtmlTextEmpty } from '@/utils/helpers';

import {
  IAttachment,
  IUser,
  IInventoryAdvancePayment,
  IMentionAttachmentComment,
  InventoryAdvancePaymentComment,
  InventoryAdvancePaymentStatus,
  InventoryAdvancePaymentStatusEnum,
  InventoryAdvancePaymentCommentAttachment
} from '@/models';

import QuillEditor from '../common/QuillEditor.vue';
import InventoryAdvancePaymentCommentListItem from './InventoryAdvancePaymentCommentListItem.vue';
import CommentAttachmentList from '@/components/common/CommentAttachmentList.vue';

export default mixins(auth, colorable).extend({
  components: {
    CommentAttachmentList,
    ErrorAlert,
    InventoryAdvancePaymentCommentListItem,
    QuillEditor
  },
  name: 'InventoryAdvancePaymentDetailActivityCard',

  props: {
    inventoryAdvancePayment: {
      type: Object as PropType<IInventoryAdvancePayment>,
      required: true
    }
  },

  data: () => ({
    loading: false,

    errorMessage: '',
    detailErrorMessage: '',
    currentTab: 0,

    inventoryAdvancePaymentCommentValid: '',
    comment: new InventoryAdvancePaymentComment(),
    commentLoading: false,
    commentErrorMessage: '',
    commentDetailErrorMessage: '',

    mentionsLoading: false,
    attachments: [] as IAttachment[]
  }),

  computed: {
    inventoryAdvancePaymentComments: get<IMentionAttachmentComment[]>(
      'inventoryAdvancePayment/getInventoryAdvancePaymentComments'
    ),

    inventoryAdvancePaymentActivities: get<Transition[]>(
      'inventoryAdvancePayment/getInventoryAdvancePaymentActivities'
    ),

    mentionableUsers: get<IUser[]>(
      'inventoryAdvancePayment/getInventoryAdvancePaymentMentionableUsers'
    ),

    getLastCreatedInventoryAdvancePaymentCommentId: get<number>(
      'inventoryAdvancePayment/getLastCreatedInventoryAdvancePaymentCommentId'
    ),

    InventoryAdvancePaymentStatus: () => InventoryAdvancePaymentStatus,

    canSaveComment(): boolean {
      return !isHtmlTextEmpty(this.comment.text) && !this.commentLoading;
    }
  },

  methods: {
    timeFromNow: (date: Date | string) => {
      return moment(date).fromNow();
    },

    ...mapActions({
      fetchInventoryAdvancePaymentActivities:
        'inventoryAdvancePayment/fetchInventoryAdvancePaymentActivities',
      fetchInventoryAdvancePaymentComments:
        'inventoryAdvancePayment/fetchInventoryAdvancePaymentComments',
      createInventoryAdvancePaymentComment:
        'inventoryAdvancePayment/createInventoryAdvancePaymentComment',
      fetchInventoryAdvancePaymentCommentsMentionableUsers:
        'inventoryAdvancePayment/fetchInventoryAdvancePaymentCommentsMentionableUsers',
      addInventoryAdvancePaymentCommentAttachments:
        'inventoryAdvancePayment/addInventoryAdvancePaymentCommentAttachments'
    }),

    addBaseAttachments(files: File[]) {
      this.attachments.push(
        ...files.map(
          file => new InventoryAdvancePaymentCommentAttachment({ file: file })
        )
      );
    },

    clearAllErrors(): void {
      this.errorMessage = '';
      this.detailErrorMessage = '';

      this.commentErrorMessage = '';
      this.commentDetailErrorMessage = '';
    },

    getInitials(name: string) {
      return name
        .toUpperCase()
        .split(' ')
        .map(n => n[0])
        .join('');
    },

    addInventoryAdvancePaymentComment() {
      this.clearAllErrors();

      this.commentLoading = true;

      const parsedCommentMentionText = InventoryAdvancePaymentComment.parseHtmlToMentionText(
        this.comment.text
      );

      return this.createInventoryAdvancePaymentComment({
        inventoryAdvancePaymentId: this.inventoryAdvancePayment.id,
        comment: { ...this.comment, text: parsedCommentMentionText }
      })
        .then(async () => {
          this.comment = new InventoryAdvancePaymentComment();
          if (this.attachments.length !== 0) {
            await this.addInventoryAdvancePaymentCommentAttachments({
              inventoryAdvancePaymentId: this.inventoryAdvancePayment.id,
              commentId: this.getLastCreatedInventoryAdvancePaymentCommentId,
              attachments: this.attachments
            });
            this.attachments = [];
          }
        })
        .then(() => {
          this.comment = new InventoryAdvancePaymentComment();
        })
        .catch(error => {
          if (error.response) {
            if (error.response.data.detail) {
              this.commentDetailErrorMessage = error.response.data.detail;
            }
          }
          this.commentErrorMessage = ErrorManager.extractApiError(error);
        })
        .finally(() => {
          this.commentLoading = false;
        });
    },

    getStatusMessage(state: InventoryAdvancePaymentStatusEnum): string {
      return InventoryAdvancePaymentStatus.getStatusMessageFromState(state);
    },

    async loadMentionableUsers() {
      this.clearAllErrors();

      this.mentionsLoading = true;
      try {
        await this.fetchInventoryAdvancePaymentCommentsMentionableUsers(
          this.inventoryAdvancePayment.id
        );
      } catch (error) {
        this.commentErrorMessage = ErrorManager.extractApiError(
          error as AxiosError
        );
      } finally {
        this.mentionsLoading = false;
      }
    }
  },

  async mounted() {
    this.loading = true;
    try {
      await this.fetchInventoryAdvancePaymentComments(
        this.inventoryAdvancePayment.id
      );
      await this.fetchInventoryAdvancePaymentActivities(
        this.inventoryAdvancePayment.id
      );
    } catch (err) {
      const error = err as AxiosError;
      if (error.response) {
        if (error.response.data.detail) {
          this.detailErrorMessage = error.response.data.detail;
        }
      }
      this.errorMessage = ErrorManager.extractApiError(error);
    } finally {
      this.loading = false;
    }
  }
});
